import React, { useEffect, useState } from "react";
import { ShieldAlert, Music2, Trash2, Save, Trophy, ShieldCheck, TrendingUp } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const Settings = () => {
  const { user, refresh } = useAuth();
  const [contact, setContact] = useState({
    name: user?.emergency_contact?.name || "",
    phone: user?.emergency_contact?.phone || "",
  });
  const [playlist, setPlaylist] = useState({
    name: user?.playlist?.name || "",
    url: user?.playlist?.url || "",
  });
  const [savingContact, setSavingContact] = useState(false);
  const [savingPlaylist, setSavingPlaylist] = useState(false);

  const score = user?.set_score;

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveContact = async (e) => {
    e?.preventDefault();
    setSavingContact(true);
    try {
      await api.patch("/users/me", {
        emergency_contact: contact.name && contact.phone ? contact : null,
      });
      await refresh();
      toast.success("Emergency contact saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingContact(false);
    }
  };

  const savePlaylist = async (e) => {
    e?.preventDefault();
    if (!playlist.url.trim()) {
      toast.error("Paste a Spotify or Apple Music link.");
      return;
    }
    setSavingPlaylist(true);
    try {
      await api.put("/users/me/playlist", playlist);
      await refresh();
      toast.success("Playlist saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save playlist.");
    } finally {
      setSavingPlaylist(false);
    }
  };

  const removePlaylist = async () => {
    try {
      await api.delete("/users/me/playlist");
      await refresh();
      setPlaylist({ name: "", url: "" });
      toast.success("Playlist removed.");
    } catch (err) {
      toast.error("Could not remove.");
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-10">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold mb-1">
          Settings
        </p>
        <h1 className="font-heading text-4xl md:text-5xl">Your Profile.</h1>
      </div>

      {/* Set Score card with circular progress ring + Discovery Boost status */}
      <section
        className="rounded-2xl border border-white/10 bg-[#111] p-6"
        data-testid="settings-set-score"
      >
        <div className="flex items-center gap-2 mb-5">
          <Trophy className="text-[#C6FF00]" size={16} />
          <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 font-semibold">
            Set Score
          </span>
        </div>

        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          <ScoreRing score={score?.score ?? 0} boosted={user?.is_boosted} />

          <div className="flex-1 w-full">
            <BoostStatus
              score={score?.score ?? 0}
              sessions={score?.sessions ?? 0}
              isBoosted={user?.is_boosted}
            />
            <div className="grid grid-cols-3 gap-3 mt-4">
              <Stat label="Sessions" value={score?.sessions ?? 0} />
              <Stat
                label="Pass rate"
                value={`${Math.round((score?.completion_rate ?? 0) * 100)}%`}
              />
              <Stat label="No-shows" value={score?.no_shows ?? 0} />
            </div>
          </div>
        </div>
      </section>

      {/* Playlist */}
      <section data-testid="settings-playlist-section">
        <div className="flex items-center gap-2 mb-3">
          <Music2 className="text-[#C6FF00]" size={16} />
          <h2 className="font-heading text-2xl">Workout Playlist</h2>
        </div>
        <p className="text-xs text-neutral-400 mb-4">
          Paste a Spotify or Apple Music playlist link. We embed it on your profile and
          during sessions.
        </p>
        <form onSubmit={savePlaylist} className="space-y-3">
          <input
            data-testid="settings-playlist-name"
            value={playlist.name}
            onChange={(e) => setPlaylist((p) => ({ ...p, name: e.target.value }))}
            placeholder="Playlist name (optional)"
            className="settings-input"
          />
          <input
            data-testid="settings-playlist-url"
            value={playlist.url}
            onChange={(e) => setPlaylist((p) => ({ ...p, url: e.target.value }))}
            placeholder="https://open.spotify.com/playlist/…"
            className="settings-input"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              data-testid="settings-playlist-save"
              disabled={savingPlaylist}
              className="bg-[#C6FF00] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-5 py-3 rounded-md disabled:opacity-60 inline-flex items-center gap-2"
            >
              <Save size={14} /> Save
            </button>
            {user?.playlist?.url && (
              <button
                type="button"
                onClick={removePlaylist}
                data-testid="settings-playlist-remove"
                className="text-xs uppercase tracking-[0.25em] text-neutral-400 hover:text-red-400 font-bold inline-flex items-center gap-1 px-3"
              >
                <Trash2 size={12} /> Remove
              </button>
            )}
          </div>
        </form>
        {user?.playlist?.embed_url && (
          <div className="mt-4 rounded-2xl overflow-hidden border border-white/10">
            <iframe
              data-testid="settings-playlist-embed"
              src={user.playlist.embed_url}
              title={user.playlist.name}
              width="100%"
              height={user.playlist.platform === "apple_music" ? "175" : "152"}
              frameBorder="0"
              allow="autoplay *; encrypted-media *; clipboard-write"
              loading="lazy"
            />
          </div>
        )}
      </section>

      {/* Emergency contact */}
      <section data-testid="settings-emergency-section">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="text-[#C6FF00]" size={16} />
          <h2 className="font-heading text-2xl">Emergency Contact</h2>
        </div>
        <p className="text-xs text-neutral-400 mb-4">
          Used by the SOS button during live sessions. Only triggered if you tap it.
        </p>
        <form onSubmit={saveContact} className="space-y-3">
          <input
            data-testid="settings-contact-name"
            value={contact.name}
            onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
            placeholder="Contact name"
            className="settings-input"
          />
          <input
            data-testid="settings-contact-phone"
            value={contact.phone}
            onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
            placeholder="+1 555 0100"
            className="settings-input"
          />
          <button
            type="submit"
            data-testid="settings-contact-save"
            disabled={savingContact}
            className="bg-[#C6FF00] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-5 py-3 rounded-md disabled:opacity-60 inline-flex items-center gap-2"
          >
            <Save size={14} /> Save
          </button>
        </form>
      </section>

      <style>{`
        .settings-input {
          width: 100%;
          background: #111111;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.5rem;
          padding: 0.75rem 1rem;
          color: #fff;
          outline: none;
          font-size: 0.9rem;
        }
        .settings-input:focus { border-color: #C6FF00; }
        .settings-input::placeholder { color: #525252; }
      `}</style>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="border-l-2 border-[#C6FF00]/60 pl-3">
    <div className="font-heading text-3xl text-white">{value}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mt-0.5">
      {label}
    </div>
  </div>
);

// Animated SVG circular progress ring around the user's Set Score.
const ScoreRing = ({ score, boosted }) => {
  const SIZE = 132;
  const STROKE = 8;
  const RADIUS = (SIZE - STROKE) / 2;
  const CIRC = 2 * Math.PI * RADIUS;
  const pct = Math.min(100, Math.max(0, score));
  const offset = CIRC - (pct / 100) * CIRC;
  const ringColor = boosted ? "#C6FF00" : "#7A8A1F";

  return (
    <div
      className="relative shrink-0"
      style={{ width: SIZE, height: SIZE }}
      data-testid="set-score-ring"
    >
      <svg width={SIZE} height={SIZE} className="-rotate-90">
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={STROKE}
          fill="none"
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          stroke={ringColor}
          strokeWidth={STROKE}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
          style={{
            transition: "stroke-dashoffset 900ms cubic-bezier(.2,.7,.2,1)",
            filter: boosted ? "drop-shadow(0 0 10px rgba(198,255,0,0.55))" : "none",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-5xl text-white leading-none">{pct}</span>
        <span className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 font-semibold mt-1">
          / 100
        </span>
      </div>
    </div>
  );
};

// Surfaces the Discovery Boost rule: score >= 80 AND sessions >= 2.
const BoostStatus = ({ score, sessions, isBoosted }) => {
  if (isBoosted) {
    return (
      <div
        data-testid="boost-status-active"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.18em] shadow-[0_0_24px_rgba(198,255,0,0.45)]"
      >
        <ShieldCheck size={12} strokeWidth={2.5} />
        Boost Active · You're top of Discover
      </div>
    );
  }
  const scoreNeeded = Math.max(0, 80 - score);
  const sessionsNeeded = Math.max(0, 2 - sessions);
  const parts = [];
  if (sessionsNeeded > 0) parts.push(`${sessionsNeeded} more Set Test${sessionsNeeded > 1 ? "s" : ""}`);
  if (scoreNeeded > 0) parts.push(`+${scoreNeeded} score`);
  return (
    <div data-testid="boost-status-inactive" className="space-y-1.5">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-neutral-300 text-[10px] font-bold uppercase tracking-[0.18em]">
        <TrendingUp size={12} strokeWidth={2.5} />
        {parts.length ? parts.join(" · ") + " to boost" : "Show up to unlock boost"}
      </div>
      <p className="text-[11px] text-neutral-500 leading-relaxed">
        Reach Set Score 80 with 2+ completed sessions and you'll surface at the
        top of everyone's Discover stack.
      </p>
    </div>
  );
};

export default Settings;
